import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { useQuery } from '@tanstack/react-query';
import { fetchSystemActivity } from '../api/systems';
import { SystemActivityEntry } from '../types';
import PanelHeader from './PanelHeader';
import { formatServerDateTime, parseServerDate } from '../services/time';
import {
  C,
  FONT,
  GLASS_BLUR,
  RAIL_RADIUS,
  depthSx,
  glintDotSx,
  glow,
  hexToRgba,
  lcarsLabelSx,
  prefersReducedMotion,
  thinScrollSx,
} from '../theme/tokens';

/** How recent a log entry has to be to count as "live" and light the trigger
 * (dock) or the fresh-count accent (inline trail). Two hours is roughly one
 * op — long enough that a scout who just logged a signature still sees it
 * called out. */
const FRESH_MS = 2 * 60 * 60 * 1000;

/**
 * The activity trail itself — loading/error/empty states plus the
 * chronological rail of entries — with no opinion on where it's mounted.
 * Shared by `CorpActivityDock` (a slide-over, for the system's own Overview
 * tab) and the Data Vault's `EntryDetail` (inline, alongside notes/tags),
 * which query the exact same `['system-activity', solarSystemId]` key so the
 * two never double-fetch when both happen to be mounted.
 */
export const ActivityTrail: React.FC<{
  solarSystemId: number;
  systemName: string;
  /** Off for an inline mount: the vault entry editor swaps entries in and
   * out with its own transitions already, and the stagger-in was designed
   * for "just summoned a dock", not "already on screen". */
  animate?: boolean;
}> = ({ solarSystemId, systemName, animate = false }) => {
  const activity = useQuery({
    queryKey: ['system-activity', solarSystemId],
    queryFn: () => fetchSystemActivity(solarSystemId),
  });
  const entries: SystemActivityEntry[] = activity.data ?? [];

  // Cyan: "actively scanning" is exactly what a loading spinner is.
  if (activity.isLoading) return <CircularProgress size={20} sx={{ color: C.cyan }} />;
  if (activity.isError) return <Typography sx={{ color: C.textDim }}>Activity trail unavailable right now.</Typography>;
  if (entries.length === 0) {
    return (
      <Stack spacing={1} sx={{ pt: 4, alignItems: 'center', textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 28, color: hexToRgba(C.wormhole, 0.4) }} />
        <Typography sx={{ color: C.textDim, fontSize: '0.88rem' }}>Nothing logged in {systemName} yet.</Typography>
        <Typography sx={{ color: C.textDim, fontSize: '0.78rem', maxWidth: 260 }}>
          Signatures, connections and site sightings the corp records here will show up in this trail.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {entries.map((a, i) => (
        <ActivityRow
          key={`${a.kind}-${a.timestamp}-${a.summary}`}
          characterName={a.characterName}
          summary={a.summary}
          timestamp={a.timestamp}
          fresh={Date.now() - (parseServerDate(a.timestamp)?.getTime() ?? 0) < FRESH_MS}
          delayMs={animate ? i * 45 : 0}
          animate={animate && !prefersReducedMotion()}
        />
      ))}
    </Stack>
  );
};

/**
 * Corp activity as a summoned dock rather than a panel in the column.
 *
 * It used to sit at the bottom of the Activity group, under the planet list —
 * the least-read slot on the page, for content that is consulted rather than
 * read. It is a *log*: "has anyone been here, and when". You want it on demand
 * while looking at something else, not occupying a third of the fold.
 *
 * So it becomes a right-edge slide-over with a trigger that carries its own
 * state: entry count, and a pulsing dot when something was logged in the last
 * couple of hours. That way the page gets quieter AND the information gets
 * louder when it actually matters — which the old static panel could never do,
 * because a panel that is always there is never noticed.
 */
const CorpActivityDock: React.FC<{ solarSystemId: number; systemName: string }> = ({ solarSystemId, systemName }) => {
  const [open, setOpen] = useState(false);
  const reduced = prefersReducedMotion();

  // Same query key ActivityTrail uses below — this is purely to decorate the
  // trigger button (count, live dot) before the dock is even open.
  const activity = useQuery({
    queryKey: ['system-activity', solarSystemId],
    queryFn: () => fetchSystemActivity(solarSystemId),
  });
  const entries: SystemActivityEntry[] = activity.data ?? [];
  const freshCount = entries.filter((a) => Date.now() - (parseServerDate(a.timestamp)?.getTime() ?? 0) < FRESH_MS).length;

  // Escape closes — a slide-over that traps you is worse than an inline panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-label={`Corp activity for ${systemName}, ${entries.length} entries`}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.4,
          cursor: 'pointer',
          appearance: 'none',
          borderRadius: `${RAIL_RADIUS}px`,
          border: `1px solid ${hexToRgba(freshCount > 0 ? C.green : C.wormhole, open ? 0.7 : 0.35)}`,
          backgroundColor: hexToRgba(freshCount > 0 ? C.green : C.wormhole, open ? 0.16 : 0.06),
          transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { boxShadow: glow(freshCount > 0 ? C.green : C.wormhole, 14, 0.35) },
          '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '2px' },
        }}
      >
        <HistoryIcon sx={{ fontSize: 14, color: freshCount > 0 ? C.green : C.wormhole }} />
        {/* Text label drops on narrow screens — the system header now packs
            this trigger alongside the star/destination icons, and the full
            label was pushing the system name onto its own wrapped line at
            390px. The button stays identified via aria-label either way. */}
        <Typography sx={{ ...lcarsLabelSx(freshCount > 0 ? C.green : C.text, '0.68rem'), display: { xs: 'none', sm: 'inline' } }}>
          Corp Activity
        </Typography>
        {entries.length > 0 && (
          <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>{entries.length}</Typography>
        )}
        {/* The live dot only appears when there is genuinely something new —
            an always-on badge is decoration and stops being read. Green
            because that is what "fresh" means everywhere else in this app
            (StatusBadge's online/fresh) — gold is reserved for a C4 class
            reading, not a generic "recent" signal. */}
        {freshCount > 0 && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: C.green, ...glintDotSx(C.green) }} />
        )}
      </Box>

      {/* Portaled to <body>: the trigger sits inside a ScanFrame, and that
          frame's backdrop-filter (frameSx) creates a new containing block
          for any `position: fixed` descendant — without the portal, the
          "site-wide" scrim and dock below render clipped to the frame's own
          box instead of the viewport. */}
      {createPortal(
        <>
          {/* Scrim: click-away close, and it dims the console so the dock reads
              as the thing in focus. Pointer-events follow `open` so the closed
              state never eats a click on the page underneath. */}
          <Box
            onClick={() => setOpen(false)}
            aria-hidden
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1200,
              backgroundColor: hexToRgba(C.bg, 0.55),
              backdropFilter: 'blur(2px)',
              opacity: open ? 1 : 0,
              pointerEvents: open ? 'auto' : 'none',
              transition: reduced ? 'none' : 'opacity 0.28s ease',
            }}
          />

          {/* Clipping viewport. The dock slides INSIDE this, so its off-screen
              resting position can never extend documentElement.scrollWidth — which
              it did before this wrapper existed, widening the page by the dock's
              own width (+388px desktop / +342px mobile, caught by the overflow
              probe). `pointer-events: none` keeps the empty region click-through;
              the dock itself opts back in. */}
          <Box
            aria-hidden={!open}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1201,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
          <Box
            role="dialog"
            aria-label={`Corp activity — ${systemName}`}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              pointerEvents: open ? 'auto' : 'none',
              width: { xs: '100%', sm: 420 },
              maxWidth: '100vw',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: hexToRgba(C.panel, 0.96),
              backdropFilter: GLASS_BLUR,
              borderLeft: `1px solid ${hexToRgba(C.wormhole, 0.35)}`,
              ...depthSx(),
              // Slid off-screen rather than unmounted, so the panel keeps its
              // scroll position between openings and the transition can play both
              // ways instead of only on enter.
              transform: open ? 'translateX(0)' : 'translateX(100%)',
              transition: reduced ? 'none' : 'transform 0.34s cubic-bezier(0.16,1,0.3,1)',
              visibility: open ? 'visible' : 'hidden',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              {/* The same header every other panel in the app draws
                  (PanelHeader) — this used to be a hand-tweaked approximation
                  in the corp identity colour (gold), which is reserved for a
                  C4 class reading elsewhere and had nothing to do with this
                  panel. Wormhole purple is the correct "this is the corp's
                  own chrome" accent. */}
              <PanelHeader
                label="Corp Activity"
                accent={C.wormhole}
                dense
                sx={{ px: 2, pt: 1.5 }}
                action={
                  <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: C.textDim, mt: -0.5, '&:hover': { color: C.text } }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                }
              />
              <Typography noWrap sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.7rem', pl: '27px', pr: 2, pt: 0.5, pb: 1.5 }}>
                {systemName}
              </Typography>
              <Box sx={{ borderBottom: `1px solid ${C.hairline}` }} />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, ...thinScrollSx }}>
              {/* Entries stagger in only while the dock is open, so the sequence
                  plays on each summon rather than once at mount. */}
              <ActivityTrail solarSystemId={solarSystemId} systemName={systemName} animate={open} />
            </Box>
          </Box>
          </Box>
        </>,
        document.body,
      )}
    </>
  );
};

/** One log line, on a timeline rail: a node per entry with the connecting
 * line running through it, so the trail reads chronologically rather than as
 * a stack of unrelated rows. */
const ActivityRow: React.FC<{
  characterName: string;
  summary: string;
  timestamp: string;
  fresh: boolean;
  delayMs: number;
  animate: boolean;
}> = ({ characterName, summary, timestamp, fresh, delayMs, animate }) => {
  // Green, matching "online/fresh" everywhere else in the app — gold means a
  // C4 wormhole class here, not recency.
  const accent = fresh ? C.green : C.wormhole;
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        position: 'relative',
        pb: 1.75,
        // The rail: drawn on every row but the last, via a pseudo-element down
        // the node column. `width` must be a '1px' string, not the bare
        // number 1 — MUI's sx sizing transform reads any unitless
        // width/height ≤ 1 as a fraction (`1` ⇒ `100%`), so this pseudo was
        // actually painting a full-width translucent slab behind every row
        // but the last instead of a hairline. Only visible once actually
        // screenshotted — this panel had never been rendered and inspected.
        '&:not(:last-of-type)::before': {
          content: '""',
          position: 'absolute',
          left: 4,
          top: 12,
          bottom: 0,
          width: '1px',
          backgroundColor: hexToRgba(C.wormhole, 0.28),
        },
        animation: animate ? `rowIn 0.4s cubic-bezier(0.16,1,0.3,1) ${delayMs}ms both` : 'none',
        '@keyframes rowIn': {
          '0%': { opacity: 0, transform: 'translateX(10px)' },
          '100%': { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          mt: '5px',
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: fresh ? accent : C.panel,
          border: `1.5px solid ${accent}`,
          boxShadow: fresh ? glow(accent, 8, 0.7) : 'none',
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: FONT.body, fontSize: '0.86rem', color: C.text, lineHeight: 1.4 }}>
          <Box component="span" sx={{ color: fresh ? accent : C.textDim, fontWeight: 600 }}>
            {characterName}
          </Box>{' '}
          reported: {summary}
        </Typography>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: fresh ? accent : C.textDim }}>
          at {formatServerDateTime(timestamp)}
        </Typography>
      </Box>
    </Stack>
  );
};

export default CorpActivityDock;
